import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchOrderId, setSearchOrderId] = useState('');
  const [searchFreelancerId, setSearchFreelancerId] = useState('');
  const [searchFreelancerName, setSearchFreelancerName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showSubmissionText, setShowSubmissionText] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Order ID',
    'Freelancer ID',
    'Freelancer Name',
    'Task ID',
    'Order Status',
    'Assigned At',
    'Due Date',
    ...(showSubmissionText ? ['Submission Text'] : []),
    'Submitted At',
    'Payout Eligible Amount',
  ];

  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = '/employee-login';
        return;
      }
      try {
        const response = await axios.get(`${apiBaseURL}/api/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(response.data);
        setFilteredOrders(response.data);
      } catch (err) {
        setError('Failed to fetch orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [apiBaseURL]);

  useEffect(() => {
    let filtered = [...orders];

    if (searchOrderId.trim()) {
      filtered = filtered.filter(order =>
        String(order.order_id || '').toLowerCase().includes(searchOrderId.toLowerCase())
      );
    }

    if (searchFreelancerId.trim()) {
      filtered = filtered.filter(order =>
        String(order.freelancer_id || '').toLowerCase().includes(searchFreelancerId.toLowerCase())
      );
    }

    if (searchFreelancerName.trim()) {
      filtered = filtered.filter(order =>
        String(order.freelancer_name || '').toLowerCase().includes(searchFreelancerName.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter(order => order.order_status === filterStatus);
    }

    if (fromDate) {
      filtered = filtered.filter(order => new Date(order.assigned_at) >= new Date(fromDate));
    }

    if (toDate) {
      const toDateEnd = new Date(toDate);
      toDateEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter(order => new Date(order.assigned_at) <= toDateEnd);
    }

    setFilteredOrders(filtered);
  }, [
    orders,
    searchOrderId,
    searchFreelancerId,
    searchFreelancerName,
    filterStatus,
    fromDate,
    toDate
  ]);

  // ✅ Utility: IST date format
  const formatToIST = dateStr =>
    new Date(dateStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

  // ✅ Final data for table and Excel
  const data = filteredOrders.map(order => ({
    order_id: order.order_id,
    freelancer_id: order.freelancer_id ?? '—',
    freelancer_name: order.freelancer_name ?? '—',
    task_id: order.task_id ?? '—',
    order_status: order.order_status,
    assigned_at: order.assigned_at ? formatToIST(order.assigned_at) : '—',
    due_date: order.due_date ? formatToIST(order.due_date) : '—',
    ...(showSubmissionText && { submission_text: order.submission_text ?? '—' }),
    submitted_at: order.submitted_at ? formatToIST(order.submitted_at) : 'Not submitted',
    payout_eligible_amount:
      order.payout_eligible_amount != null && !isNaN(order.payout_eligible_amount)
        ? Number(order.payout_eligible_amount).toFixed(2)
        : '0.00',
  }));

  const exportToExcel = () => {
    const now = new Date();
    const formattedTime = now.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    const timestampForFile = now.toISOString().replace(/[:T]/g, '-').split('.')[0];

    const wsData = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Order Info'],
      [`Downloaded on: ${formattedTime}`],
      [],
      columns,
      ...data.map(row => columns.map(col => row[col.toLowerCase().replace(/ /g, '_')]))
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
    XLSX.writeFile(workbook, `Freelancer_Order_Info_${timestampForFile}.xlsx`);
  };

  const uniqueStatuses = [...new Set(orders.map(order => order.order_status))];

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 align-top max-w-xs">
                    {col === 'Submission Text' ? (
                      <div
                        className="max-h-60 overflow-auto border p-2 bg-gray-50 rounded text-sm"
                        dangerouslySetInnerHTML={{ __html: row[col.toLowerCase().replace(/ /g, '_')] }}
                      />
                    ) : (
                      row[col.toLowerCase().replace(/ /g, '_')]
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div className="p-6">Loading orders...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100" style={{ marginLeft: '16rem' }}>
      <h2 className="text-2xl font-semibold mb-4">Orders</h2>

      <div className="flex flex-wrap gap-4 mb-4 items-center">
        <input
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <input
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <input
          type="text"
          placeholder="Search by Order ID"
          value={searchOrderId}
          onChange={e => setSearchOrderId(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <input
          type="text"
          placeholder="Search by Freelancer ID"
          value={searchFreelancerId}
          onChange={e => setSearchFreelancerId(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <input
          type="text"
          placeholder="Search by Freelancer Name"
          value={searchFreelancerName}
          onChange={e => setSearchFreelancerName(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="border px-3 py-2 rounded"
        >
          <option value="">All Statuses</option>
          {uniqueStatuses.map((status, index) => (
            <option key={index} value={status}>{status}</option>
          ))}
        </select>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showSubmissionText}
            onChange={() => setShowSubmissionText(!showSubmissionText)}
            className="form-checkbox h-5 w-5 text-blue-600"
          />
          <span className="text-sm">Show Submission Text</span>
        </label>

        <button
          onClick={exportToExcel}
          className="border px-3 py-2 rounded bg-green-500 text-white"
        >
          Export to Excel
        </button>
      </div>

      <div className="mb-2 text-gray-600">Total Orders: {filteredOrders.length}</div>

      <Table columns={columns} data={data} />
    </div>
  );
};

export default OrdersPage;
